import React, { useState } from 'react';
import { Reminder, TriggerRelation } from '../../../../types/task';
import {
  IOSFormGroup,
  IOSFormItem,
  IOSFormLabel,
  IOSFormToggle,
  IOSFormDateTime,
  IOSFormDivider
} from './IOSFormGroup';
import {
  IOSTriggerTypeSelector,
  IOSRelationSelector,
  IOSTimeOffsetInput,
  IOSReminderMethodSelector
} from './IOSReminderComponents';
import {
  secondsToValueUnit,
  valueUnitToSeconds,
  getReminderDescription,
  validateReminder,
  createDefaultReminder,
  formatReminderMethods,
  formatAbsoluteTimeForInput,
  parseInputToAbsoluteTime
} from '../../utils/reminderUtils';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { userPreferencesAtom } from '../../../../atoms/v3/userPreferencesAtoms';

interface IOSRemindersEditorProps {
  reminders: Reminder[];
  onChange: (reminders: Reminder[]) => void;
  isDisabled?: boolean;
  // 任务时间信息，用于验证相对时间提醒的有效性
  taskTimes?: {
    startAt?: string | null;
    dueAt?: string | null;
    cost?: number | null;
  };
}

/**
 * iOS风格的提醒编辑器组件
 */
export const IOSRemindersEditor: React.FC<IOSRemindersEditorProps> = ({
  reminders,
  onChange,
  isDisabled = false,
  taskTimes
}) => {
  const { t } = useTranslation(['v3/ios', 'common']);
  const userPreferences = useAtomValue(userPreferencesAtom);

  // 如果有 reminders 数据，默认显示编辑器；否则默认隐藏
  const [showEditor, setShowEditor] = useState(reminders.length > 0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // 当 reminders 数据变化时，更新 showEditor 状态
  React.useEffect(() => {
    // 如果从无 reminders 变为有 reminders，自动显示编辑器
    if (reminders.length > 0 && !showEditor) {
      setShowEditor(true);
    }
  }, [reminders.length, showEditor]);

  // 添加提醒
  const handleAddReminder = () => {
    const newReminder = createDefaultReminder(userPreferences);
    onChange([...reminders, newReminder]);
    setEditingIndex(reminders.length); // 编辑新添加的提醒
  };

  // 删除提醒
  const handleRemoveReminder = (index: number) => {
    const newReminders = [...reminders];
    newReminders.splice(index, 1);
    onChange(newReminders);
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  // 检查相对时间类型是否可用
  const isRelationAvailable = (relation: TriggerRelation): boolean => {
    if (!taskTimes) return true; // 如果没有提供任务时间信息，则不限制

    switch (relation) {
      case TriggerRelation.START:
        return !!taskTimes.startAt;
      case TriggerRelation.DUE:
        return !!taskTimes.dueAt;
      case TriggerRelation.CALCULATED_END:
        return !!(taskTimes.startAt && taskTimes.cost);
      default:
        return true;
    }
  };

  // 更新提醒
  const handleUpdateReminder = (index: number, changes: Partial<Reminder> | { trigger: Partial<Reminder['trigger']> }) => {
    const newReminders = [...reminders];
    const currentReminder = { ...newReminders[index] };

    if ('trigger' in changes && typeof changes.trigger === 'object') {
      // 深度合并 trigger 变更
      currentReminder.trigger = { ...currentReminder.trigger, ...changes.trigger };
    } else {
      // 合并顶级变更
      Object.assign(currentReminder, changes);
    }

    // 根据触发类型清理不需要的字段
    const currentTrigger = currentReminder.trigger;
    if (currentTrigger.type === 'ABSOLUTE') {
      if (!currentTrigger.absoluteTime) {
        currentTrigger.absoluteTime = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
      }
      delete currentTrigger.related;
      delete currentTrigger.offsetSeconds;
    } else { // RELATIVE
      if (currentTrigger.related === undefined || !Object.values(TriggerRelation).includes(currentTrigger.related)) {
        // 选择第一个可用的关系类型
        const availableRelations = Object.values(TriggerRelation).filter(isRelationAvailable);
        currentTrigger.related = availableRelations.length > 0 ? availableRelations[0] : TriggerRelation.START;
      }
      if (currentTrigger.offsetSeconds === undefined) {
        currentTrigger.offsetSeconds = -900;
      }
      delete currentTrigger.absoluteTime;
    }

    newReminders[index] = currentReminder;
    onChange(newReminders);
  };

  // 处理相对时间偏移变更
  const handleOffsetChange = (index: number, value: number, unit: string, direction: 'before' | 'after') => {
    const offsetSeconds = valueUnitToSeconds(value, unit, direction === 'before');
    handleUpdateReminder(index, { trigger: { offsetSeconds } });
  };

  // 切换编辑状态
  const toggleEditing = (index: number) => {
    setEditingIndex(editingIndex === index ? null : index);
  };

  return (
    <IOSFormGroup>
      <IOSFormItem className="flex items-center justify-between">
        <IOSFormLabel>{t('v3/ios:remindersEditor.title')}</IOSFormLabel>
        <IOSFormToggle
          checked={showEditor}
          onChange={setShowEditor}
          disabled={isDisabled}
        />
      </IOSFormItem>

      {showEditor && (
        <>
          {reminders.length > 0 ? (
            reminders.map((reminder, index) => {
              const isEditing = editingIndex === index;
              const isRelative = reminder.trigger.type === 'RELATIVE';
              const { value: timeValue, unit: timeUnit } = isRelative
                ? secondsToValueUnit(reminder.trigger.offsetSeconds || 0)
                : { value: 0, unit: 'minutes' as const };
              const direction = isRelative && (reminder.trigger.offsetSeconds || 0) < 0 ? 'before' : 'after';

              return (
                <div key={index}>
                  <IOSFormItem className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-ios-body">
                        {getReminderDescription(reminder, t)}
                      </div>
                      <div className="text-xs text-ios-gray dark:text-ios-dark-gray mt-1">
                        {formatReminderMethods(reminder.methods)}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => toggleEditing(index)}
                        disabled={isDisabled}
                        className="text-ios-blue dark:text-ios-dark-blue disabled:opacity-50 px-2 py-1 h-8 flex items-center justify-center"
                      >
                        {isEditing ? t('v3/ios:remindersEditor.done') : t('v3/ios:remindersEditor.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveReminder(index)}
                        disabled={isDisabled}
                        className="text-ios-red dark:text-ios-dark-red disabled:opacity-50 px-2 py-1 h-8 flex items-center justify-center"
                      >
                        {t('v3/ios:remindersEditor.delete')}
                      </button>
                    </div>
                  </IOSFormItem>

                  {isEditing && (
                    <>
                      <IOSFormDivider />

                      {/* 触发类型选择 */}
                      <IOSFormItem>
                        <IOSFormLabel className="block mb-2">
                          {t('v3/ios:remindersEditor.triggerType')}
                        </IOSFormLabel>
                        <IOSTriggerTypeSelector
                          value={reminder.trigger.type}
                          onChange={(type) => handleUpdateReminder(index, { trigger: { type } })}
                          disabled={isDisabled}
                        />
                      </IOSFormItem>

                      <IOSFormDivider />

                      {/* 相对时间设置 */}
                      {isRelative && (
                        <>
                          <IOSFormItem>
                            <IOSFormLabel className="block mb-2">
                              {t('v3/ios:remindersEditor.relativeTo')}
                            </IOSFormLabel>
                            <IOSRelationSelector
                              value={reminder.trigger.related || TriggerRelation.START}
                              onChange={(related) => handleUpdateReminder(index, { trigger: { related } })}
                              disabled={isDisabled}
                              isRelationAvailable={isRelationAvailable}
                            />
                          </IOSFormItem>

                          <IOSFormDivider />

                          <IOSFormItem>
                            <IOSFormLabel className="block mb-2">
                              {t('v3/ios:remindersEditor.offset')}
                            </IOSFormLabel>
                            <IOSTimeOffsetInput
                              value={timeValue}
                              unit={timeUnit}
                              direction={direction}
                              onValueChange={(value) => handleOffsetChange(index, value, timeUnit, direction)}
                              onUnitChange={(unit) => handleOffsetChange(index, timeValue, unit, direction)}
                              onDirectionChange={(dir) => handleOffsetChange(index, timeValue, timeUnit, dir)}
                              disabled={isDisabled}
                            />
                          </IOSFormItem>

                          <IOSFormDivider />
                        </>
                      )}

                      {/* 绝对时间设置 */}
                      {!isRelative && (
                        <>
                          <IOSFormItem>
                            <IOSFormLabel className="block mb-2">
                              {t('v3/ios:remindersEditor.specificTime')}
                            </IOSFormLabel>
                            <IOSFormDateTime
                              value={reminder.trigger.absoluteTime}
                              onChange={(absoluteTime) => handleUpdateReminder(index, { trigger: { absoluteTime } })}
                              disabled={isDisabled}
                            />
                          </IOSFormItem>

                          <IOSFormDivider />
                        </>
                      )}

                      {/* 提醒方式选择 */}
                      <IOSFormItem>
                        <IOSFormLabel className="block mb-2">
                          {t('v3/ios:remindersEditor.methods')}
                        </IOSFormLabel>
                        <IOSReminderMethodSelector
                          selectedMethods={reminder.methods}
                          onChange={(methods) => handleUpdateReminder(index, { methods })}
                          disabled={isDisabled}
                          showUserPreferenceHint={true}
                          userPreferredMethods={userPreferences?.defaultReminderMethods || []}
                        />
                      </IOSFormItem>
                    </>
                  )}
                </div>
              );
            })
          ) : (
            <IOSFormItem className="text-center text-ios-gray dark:text-ios-dark-gray">
              {t('v3/ios:remindersEditor.noReminders')}
            </IOSFormItem>
          )}

          <IOSFormDivider />

          <IOSFormItem>
            <button
              type="button"
              onClick={handleAddReminder}
              disabled={isDisabled}
              className="w-full py-3 text-ios-blue dark:text-ios-dark-blue text-center disabled:opacity-50 font-medium"
            >
              + {t('v3/ios:remindersEditor.addReminder')}
            </button>
          </IOSFormItem>
        </>
      )}
    </IOSFormGroup>
  );
};
